import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAIAssistantStore, ChatMessage, ChatSession } from '../stores/aiAssistantStore';
import { useAppStore } from '../stores/appStore';
import { toast } from 'sonner';

/**
 * AI Assistant API Integration Hook
 * Backend API Base: /api/ai-assistant/*
 */

interface ProjectContext {
  caseId?: string;
  projectName?: string;
  companyName?: string;
  businessDomain?: string;
  currentPhase?: string;
  strategicQuestions?: string[];
  enterpriseSystems?: string[];
}

interface QueryRequest {
  query: string;
  userId: string;
  sessionId?: string;
  projectContext?: ProjectContext;
}

interface QueryResponse {
  messageId: string;
  sessionId: string;
  text: string;
  responseFormat: 'mbb_pyramid' | 'conversational' | 'structured' | 'technical' | 'fallback' | 'text';
  confidence?: {
    overall_score: number;
    overall_level: 'High' | 'Medium' | 'Low';
  };
  sources?: string[];
  executionTimeMs: number;
  timestamp: string;
  queryClassification?: any;
  orchestrationDetails?: any;
}

interface FeedbackRequest {
  messageId: string;
  rating: 'thumbs_up' | 'thumbs_down';
  comment?: string;
}

interface ChatHistoryResponse {
  sessions: ChatSession[];
  total: number;
}

export const useAIAssistant = () => {
  const queryClient = useQueryClient();
  const currentUser = useAppStore((state) => state.currentUser);
  const {
    currentSessionId,
    setLoading,
    setError,
    addMessage,
    updateMessage,
    addSession,
    updateSession,
    setSessions,
  } = useAIAssistantStore();

  // Query AI Assistant
  const queryMutation = useMutation({
    mutationFn: async (request: QueryRequest) => {
      return api.post<QueryResponse>('/ai-assistant/query', request, {
        timeout: 120000, // 2 minutes for AI Assistant queries
      });
    },
    onMutate: () => {
      setLoading(true);
      setError(null);
    },
    onSuccess: (response, variables) => {
      // Add user message
      const userMessage: ChatMessage = {
        messageId: `user-${Date.now()}`,
        sessionId: response.sessionId,
        role: 'user',
        content: variables.query,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };
      addMessage(userMessage);

      // Add assistant message
      const assistantMessage: ChatMessage = {
        messageId: response.messageId,
        sessionId: response.sessionId,
        role: 'assistant',
        content: response.text,
        responseFormat: response.responseFormat,
        sources: response.sources,
        confidence: response.confidence,
        executionTimeMs: response.executionTimeMs,
        timestamp: response.timestamp,
        createdAt: response.timestamp,
        queryClassification: response.queryClassification,
        orchestrationDetails: response.orchestrationDetails,
      };
      addMessage(assistantMessage);

      // Update session
      updateSession(response.sessionId, {
        updatedAt: new Date().toISOString(),
        messageCount: 2, // Will be updated by backend
        lastMessage: variables.query,
      });

      setLoading(false);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to process query';
      setError(errorMessage);
      setLoading(false);
      toast.error(errorMessage);
    },
  });

  // Submit feedback
  const feedbackMutation = useMutation({
    mutationFn: async (request: FeedbackRequest) => {
      return api.post('/ai-assistant/feedback', request);
    },
    onSuccess: (_, variables) => {
      // Update message with feedback
      updateMessage(variables.messageId, {
        feedback: {
          rating: variables.rating,
          comment: variables.comment,
        },
      });
      toast.success('Thank you for your feedback!');
    },
    onError: () => {
      toast.error('Failed to submit feedback');
    },
  });

  // Fetch chat history
  const useChatHistory = (userId?: string, limit: number = 50) => {
    const query = useQuery({
      queryKey: ['ai-assistant-history', userId],
      queryFn: async () => {
        if (!userId) return { sessions: [], total: 0 };
        return api.get<ChatHistoryResponse>(`/ai-assistant/history/${userId}`, {
          limit,
          include_messages: false,
        });
      },
      enabled: !!userId,
      staleTime: 1 * 60 * 1000, // 1 minute
    });

    // Update store when data changes
    useEffect(() => {
      if (query.data) {
        setSessions(query.data.sessions);
      }
    }, [query.data]);

    return query;
  };

  // Fetch session messages
  const useSessionMessages = (sessionId?: string) => {
    return useQuery({
      queryKey: ['ai-assistant-session-messages', sessionId],
      queryFn: async () => {
        if (!sessionId) return [];
        const response = await api.get<ChatHistoryResponse>(
          `/ai-assistant/history/${currentUser?.id}`,
          {
            limit: 1,
            include_messages: true,
          }
        );
        const session = response.sessions.find((s) => s.sessionId === sessionId);
        return session?.messages || [];
      },
      enabled: !!sessionId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Health check
  const useHealthCheck = () => {
    return useQuery({
      queryKey: ['ai-assistant-health'],
      queryFn: async () => {
        return api.get('/ai-assistant/health');
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
    });
  };

  // Send query helper
  const sendQuery = async (query: string, sessionId?: string) => {
    if (!currentUser?.id) {
      toast.error('User not authenticated');
      return;
    }

    return queryMutation.mutateAsync({
      query,
      userId: currentUser.id,
      sessionId: sessionId || currentSessionId || undefined,
    });
  };

  // Submit feedback helper
  const submitFeedback = async (
    messageId: string,
    rating: 'thumbs_up' | 'thumbs_down',
    comment?: string
  ) => {
    return feedbackMutation.mutateAsync({ messageId, rating, comment });
  };

  return {
    // Actions
    sendQuery,
    submitFeedback,

    // State
    isLoading: queryMutation.isPending,
    error: queryMutation.error,

    // Hooks
    useChatHistory,
    useSessionMessages,
    useHealthCheck,

    // Mutations
    queryMutation,
    feedbackMutation,
  };
};

// Streaming response types
interface StreamEventHandlers {
  onStart?: (data: { messageId: string; sessionId: string }) => void;
  onStatus?: (data: { step: number; message: string; icon: string }) => void;
  onClassification?: (data: {
    intent: string;
    responseFormat: string;
    confidence: number;
    contexts?: string[];
    complexity?: string;
  }) => void;
  onProvider?: (data: { provider: string }) => void;
  onContent?: (chunk: string) => void;
  onMetadata?: (data: {
    responseFormat: string;
    confidence: { overall_score: number; overall_level: string };
    sourcesUsed: string[];
    executionTimeMs: number;
    orchestrationDetails?: {
      sourcesQueried?: string[];
      sourcesSucceeded?: string[];
      sourcesFailed?: string[];
      neo4jQuery?: string;
      webSearchQuery?: string;
      parallelExecution?: boolean;
      totalDurationMs?: number;
    };
  }) => void;
  onFollowUp?: (data: { questions: string[] }) => void;
  onDone?: (data: { messageId: string; totalChars: number }) => void;
  onError?: (error: string) => void;
}

// Suggestion types
interface SuggestionItem {
  question: string;
  category: string;
  confidence: number;
  sourceHint: string;
}

interface SuggestionsResponse {
  recent_topics: SuggestionItem[];
  trending: SuggestionItem[];
  project_specific: SuggestionItem[] | null;
  personalized: SuggestionItem[];
}

// AI Assistant API methods for direct use
export const aiAssistantAPI = {
  /**
   * Send a query to the AI Assistant
   */
  query: async (request: QueryRequest) => {
    return api.post<QueryResponse>('/ai-assistant/query', request, {
      timeout: 120000, // 2 minutes
    });
  },

  /**
   * Stream a query response using Server-Sent Events
   */
  streamQuery: async (
    request: QueryRequest,
    handlers: StreamEventHandlers
  ): Promise<void> => {
    // Use same base URL pattern as api.ts - already includes /api suffix
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://graph-solution-backend.huhoka.com/api';

    const response = await fetch(`${baseUrl}/ai-assistant/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const eventText of events) {
          if (!eventText.trim()) continue;

          const lines = eventText.split('\n');
          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7);
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);

            switch (eventType) {
              case 'start':
                handlers.onStart?.(data);
                break;
              case 'status':
                handlers.onStatus?.(data);
                break;
              case 'classification':
                handlers.onClassification?.(data);
                break;
              case 'provider':
                handlers.onProvider?.(data);
                break;
              case 'content':
                handlers.onContent?.(data.chunk);
                break;
              case 'metadata':
                handlers.onMetadata?.(data);
                break;
              case 'followup':
                handlers.onFollowUp?.(data);
                break;
              case 'done':
                handlers.onDone?.(data);
                break;
              case 'error':
                handlers.onError?.(data.error);
                break;
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /**
   * Get chat history for a user
   */
  getHistory: async (userId: string, limit: number = 50, includeMessages: boolean = false) => {
    return api.get<ChatHistoryResponse>(`/ai-assistant/history/${userId}`, {
      limit,
      include_messages: includeMessages,
    });
  },

  /**
   * Submit feedback for a message
   */
  submitFeedback: async (request: FeedbackRequest) => {
    return api.post('/ai-assistant/feedback', request);
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    return api.get('/ai-assistant/health');
  },

  /**
   * Get personalized suggestions for welcome screen
   */
  getSuggestions: async (userId: string, caseId?: string, limit: number = 3) => {
    const params: Record<string, string | number> = { limit };
    if (caseId) {
      params.case_id = caseId;
    }
    return api.get<SuggestionsResponse>(`/ai-assistant/suggestions/${userId}`, params);
  },
};

// Export types for use in components
export type { SuggestionItem, SuggestionsResponse };
